import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AppWindow, ListChecks, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSoftwareList, type SoftwareEntry, type SoftwareProblemType } from '../lib/software';
import { useToast } from './toast';
import { Badge, Button, Card, CardHeader, ConfirmDialog, EmptyState, ErrorState, Field, Input, Modal, PageLoader, Switch, Table, Td, Textarea, Th } from './ui';

type Draft = { id?: string; name: string; publisher: string; notes: string; offered: boolean };

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * Settings → Software: the programs and services the school supports. People choose from it when they report a problem,
 * and knowledgebase articles are tagged with it.
 */
export function SoftwareSettings() {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const toast = useToast();
  const queryClient = useQueryClient();
  const list = useSoftwareList();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleting, setDeleting] = useState<SoftwareEntry | null>(null);
  const [problemsFor, setProblemsFor] = useState<string | null>(null);

  // Articles and tickets show the names, so renames show up there too.
  const refresh = () => {
    for (const key of ['software', 'knowledge', 'work-orders', 'work-order']) void queryClient.invalidateQueries({ queryKey: [key] });
  };

  const save = useMutation({
    mutationFn: ({ id, ...fields }: Draft) => {
      const body = { ...fields, publisher: fields.publisher.trim() || null, notes: fields.notes.trim() || null };
      return id ? api.patch<SoftwareEntry>(`/software/${id}`, body) : api.post<SoftwareEntry>('/software', body);
    },
    onSuccess: (saved, { id }) => {
      refresh();
      setDraft(null);
      toast.success(id ? `${saved.name} saved` : `${saved.name} added`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const setOffered = useMutation({
    mutationFn: ({ id, offered }: { id: string; offered: boolean }) => api.patch<SoftwareEntry>(`/software/${id}`, { offered }),
    onSuccess: (saved) => {
      refresh();
      setDeleting(null);
      toast.success(saved.offered ? `${saved.name} is offered on new tickets` : `${saved.name} is no longer offered on new tickets`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (entry: SoftwareEntry) => api.delete(`/software/${entry.id}`),
    onSuccess: (_, entry) => {
      refresh();
      setDeleting(null);
      toast.success(`${entry.name} removed`);
    },
    onError: (err) => {
      setDeleting(null);
      toast.error(errorMessage(err));
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (draft) save.mutate(draft);
  };

  const entries = list.data ?? [];
  // Software with tickets stays on the list, so those tickets keep it. It can stop being offered instead.
  const inUse = !!deleting && deleting.ticketCount > 0;

  return (
    <Card>
      <CardHeader
        title="Software"
        description="The programs and services the school supports. People choose from this list when they report a problem, and knowledgebase articles are tagged with it."
        actions={
          <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setDraft({ name: '', publisher: '', notes: '', offered: true })}>
            Add software
          </Button>
        }
      />

      {list.isLoading ? (
        <PageLoader />
      ) : list.error ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : entries.length === 0 ? (
        <EmptyState
          icon={<AppWindow />}
          title="No software yet"
          description="Add the apps and services people use, like Google Classroom or PowerSchool, so they can report a problem with one."
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Software</Th>
              <Th>New tickets</Th>
              <Th>Problem types</Th>
              <Th>Articles</Th>
              <Th>Tickets</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {entries.map((entry) => (
              <tr key={entry.id}>
                <Td className="max-w-[24rem]">
                  <p className="font-medium text-slate-900">{entry.name}</p>
                  {entry.publisher && <p className="text-xs text-slate-500">{entry.publisher}</p>}
                  {entry.notes && <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{entry.notes}</p>}
                </Td>
                <Td>{entry.offered ? <Badge tone="green">Offered</Badge> : <Badge>Not offered</Badge>}</Td>
                <Td>
                  <Button size="sm" variant="ghost" icon={<ListChecks />} onClick={() => setProblemsFor(entry.id)}>
                    {entry.problemTypes.length === 0 ? 'Set them up' : plural(entry.problemTypes.length, 'problem')}
                  </Button>
                </Td>
                <Td>
                  {entry.articleCount > 0 ? (
                    <Link to={`/knowledge?softwareId=${entry.id}`} title="Knowledgebase articles about it" className="text-brand-700 hover:underline">
                      {entry.articleCount}
                    </Link>
                  ) : (
                    0
                  )}
                </Td>
                <Td>{entry.ticketCount}</Td>
                <Td className="text-right whitespace-nowrap">
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Pencil />}
                    onClick={() => setDraft({ id: entry.id, name: entry.name, publisher: entry.publisher ?? '', notes: entry.notes ?? '', offered: entry.offered })}
                  >
                    Edit
                  </Button>
                  {isAdmin && (
                    <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(entry)}>
                      Delete
                    </Button>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal
        open={!!draft}
        onClose={() => setDraft(null)}
        size="sm"
        title={draft?.id ? 'Edit software' : 'Add software'}
        footer={
          <>
            <Button onClick={() => setDraft(null)}>Cancel</Button>
            <Button variant="primary" type="submit" form="software-form" loading={save.isPending}>
              Save
            </Button>
          </>
        }
      >
        {draft && (
          <form id="software-form" onSubmit={submit} className="space-y-4">
            <Field label="Name" required hint={draft.id ? 'Its tickets and articles show the new name too' : undefined}>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} autoFocus required maxLength={120} placeholder="e.g. Google Classroom" />
            </Field>
            <Field label="Publisher" hint="Optional">
              <Input value={draft.publisher} onChange={(e) => setDraft({ ...draft, publisher: e.target.value })} maxLength={120} placeholder="e.g. Google" />
            </Field>
            <Field label="Notes" hint="Optional - only the technology team sees these">
              <Textarea rows={2} maxLength={1000} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
            </Field>
            <Switch
              checked={draft.offered}
              onChange={(offered) => setDraft({ ...draft, offered })}
              label="Offer on new tickets"
              description="People can choose it when they report a problem. Turn this off for software the school no longer supports - its tickets and articles keep it."
            />
          </form>
        )}
      </Modal>

      <ProblemTypes entry={entries.find((entry) => entry.id === problemsFor) ?? null} onClose={() => setProblemsFor(null)} onSaved={refresh} />

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          if (!inUse) remove.mutate(deleting);
          else if (deleting.offered) setOffered.mutate({ id: deleting.id, offered: false });
          else setDeleting(null);
        }}
        loading={remove.isPending || setOffered.isPending}
        tone={inUse && !deleting?.offered ? 'primary' : 'danger'}
        title={deleting ? (inUse ? `${deleting.name} is in use` : `Delete ${deleting.name}?`) : ''}
        description={
          !deleting
            ? ''
            : inUse
              ? deleting.offered
                ? `${plural(deleting.ticketCount, 'work order')} ${deleting.ticketCount === 1 ? 'is' : 'are'} about it, so it stays on the list. Stop offering it on new tickets instead?`
                : `${plural(deleting.ticketCount, 'work order')} ${deleting.ticketCount === 1 ? 'is' : 'are'} about it, so it stays on the list. It’s already not offered on new tickets.`
              : deleting.articleCount > 0
                ? `It comes off ${plural(deleting.articleCount, 'knowledgebase article')}. ${deleting.articleCount === 1 ? 'The article stays' : 'The articles stay'}.`
                : 'Nothing uses it yet.'
        }
        confirmLabel={inUse ? (deleting?.offered ? 'Stop offering it' : 'OK') : 'Delete'}
      />
    </Card>
  );
}


/**
 * The problems people can report with one piece of software, which take the place of the device categories under
 * "Type of problem" on its tickets. Software with an empty list uses those categories instead.
 */
function ProblemTypes({ entry, onClose, onSaved }: { entry: SoftwareEntry | null; onClose(): void; onSaved(): void }) {
  const toast = useToast();
  const [adding, setAdding] = useState('');
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [removing, setRemoving] = useState<SoftwareProblemType | null>(null);

  const done = (message: string) => {
    onSaved();
    toast.success(message);
  };

  const add = useMutation({
    mutationFn: (name: string) => api.post<SoftwareEntry>(`/software/${entry!.id}/problem-types`, { name }),
    onSuccess: (_, name) => {
      setAdding('');
      done(`Added “${name}”`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const save = useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; offered?: boolean }) => api.patch<SoftwareEntry>(`/software/problem-types/${id}`, body),
    onSuccess: (_, { name, offered }) => {
      setRenaming(null);
      done(name !== undefined ? `Renamed to “${name}”` : offered ? 'Offered on new tickets' : 'No longer offered on new tickets');
    },
    onError: (err) => {
      setRenaming(null);
      toast.error(errorMessage(err));
    },
  });

  const remove = useMutation({
    mutationFn: (problem: SoftwareProblemType) => api.delete(`/software/problem-types/${problem.id}`),
    onSuccess: (_, problem) => {
      setRemoving(null);
      done(`Removed “${problem.name}”`);
    },
    onError: (err) => {
      setRemoving(null);
      toast.error(errorMessage(err));
    },
  });

  /** Renaming saves when the field loses focus or Enter is pressed, like the other inline lists. */
  const rename = (problem: SoftwareProblemType) => {
    if (!renaming || renaming.id !== problem.id) return;
    const name = renaming.name.trim();
    if (!name || name === problem.name) setRenaming(null);
    else save.mutate({ id: problem.id, name });
  };

  return (
    <>
      <Modal open={!!entry} onClose={onClose} size="sm" title={entry ? `What can go wrong with ${entry.name}?` : ''} footer={<Button onClick={onClose}>Done</Button>}>
        {entry && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              These take the place of the usual problem types when someone reports a problem with {entry.name}, and workflows can act on them. Leave the
              list empty to use the device problem types instead.
            </p>
            {entry.problemTypes.length > 0 && (
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {entry.problemTypes.map((problem) => (
                  <li key={problem.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                    <Input
                      className="min-w-0 flex-1"
                      value={renaming?.id === problem.id ? renaming.name : problem.name}
                      maxLength={120}
                      aria-label={`Problem type: ${problem.name}`}
                      onChange={(event) => setRenaming({ id: problem.id, name: event.target.value })}
                      onBlur={() => rename(problem)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          rename(problem);
                        }
                      }}
                    />
                    <Switch checked={problem.offered} onChange={(offered) => save.mutate({ id: problem.id, offered })} label={problem.offered ? 'Offered' : 'Hidden'} />
                    <Button size="sm" variant="ghost" icon={<Trash2 />} aria-label={`Remove ${problem.name}`} onClick={() => setRemoving(problem)} />
                  </li>
                ))}
              </ul>
            )}
            <form
              className="flex gap-2"
              onSubmit={(event: FormEvent) => {
                event.preventDefault();
                const name = adding.trim();
                if (name) add.mutate(name);
              }}
            >
              <Input
                className="flex-1"
                value={adding}
                onChange={(event) => setAdding(event.target.value)}
                maxLength={120}
                placeholder="e.g. Can’t sign in"
                aria-label="A problem to add"
              />
              <Button type="submit" variant="primary" icon={<Plus />} loading={add.isPending} disabled={!adding.trim()}>
                Add
              </Button>
            </form>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing)}
        loading={remove.isPending}
        tone="danger"
        title={removing ? `Remove “${removing.name}”?` : ''}
        description={
          removing?.ticketCount
            ? `${plural(removing.ticketCount, 'work order')} ${removing.ticketCount === 1 ? 'is' : 'are'} about it, so it stays. Turn Offered off instead to keep it off new tickets.`
            : 'Nothing has been reported under it yet.'
        }
        confirmLabel="Remove"
      />
    </>
  );
}
